import { useState, useEffect, useMemo } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { apiFetch } from '../utils/api';
import CostTrackerWidget from '../components/CostTrackerWidget';
import {
  LayoutDashboard,
  MessageSquare,
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  ChevronDown,
  ChevronRight,
  Upload,
  Database,
  Settings,
  Store,
  Sparkles,
  Bell,
  Film,
  CheckCircle,
  Zap,
  Gift,
  FileImage,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WHATS_NEW_ITEMS = [
  {
    icon: Zap,
    title: 'SKU-Level Live Tracking',
    description: 'Track real-time ratings & review counts per SKU on Channel Dashboards.',
    date: 'Apr 2026',
    isNew: true,
  },
  {
    icon: CheckCircle,
    title: 'Enhanced Review Counts',
    description: 'Scrapers now capture both rating counts and text review counts separately.',
    date: 'Apr 2026',
    isNew: true,
  },
  {
    icon: Film,
    title: 'Auto-Fetch Scheduler',
    description: 'Set automatic scraping intervals from Settings — 1h, 6h, 12h, or 24h.',
    date: 'Mar 2026',
    isNew: false,
  },
  {
    icon: Gift,
    title: 'O.R.I.O.N API Integration',
    description: 'External API endpoints for cross-app data sharing with x-api-key auth.',
    date: 'Mar 2026',
    isNew: false,
  },
  {
    icon: FileImage,
    title: 'Brand & Platform Logos',
    description: 'Custom logos and icons across breadcrumbs, cards, and navigation.',
    date: 'Mar 2026',
    isNew: false,
  },
  {
    icon: Zap,
    title: 'Batch AI Analysis',
    description: 'Analyse thousands of reviews automatically with Groq-powered sentiment AI.',
    date: 'Mar 2026',
    isNew: false,
  },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [brands, setBrands] = useState([]);
  const [brandLogos, setBrandLogos] = useState({});
  const [expandedBrands, setExpandedBrands] = useState(false);

  // Dedupe brands by fully-normalized brand name so production duplicates
  // (casing, trailing/NBSP/zero-width whitespace variants) collapse to a
  // single sidebar entry. Backend also normalizes, this is defensive.
  //
  // Rule (must mirror server._norm_brand):
  //   - NBSP (\u00A0) is a real space → replaced with a space
  //   - Zero-width family (\u200B \u200C \u200D \uFEFF) is DELETED
  //   - Collapse remaining whitespace, trim, lowercase.
  const visibleBrands = useMemo(() => {
    const zeroWidth = /[\u200B\u200C\u200D\uFEFF]/g;
    const normalize = (s) => (s || '')
      .replace(zeroWidth, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    const byKey = new Map();
    for (const b of brands) {
      if (!b?.brand) continue;
      const key = normalize(b.brand);
      if (!key || key === 'unknown') continue;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, b);
      } else if ((b.total_reviews || 0) > (existing.total_reviews || 0)) {
        // Keep the row with more reviews so counts don't shrink
        byKey.set(key, b);
      }
    }
    return Array.from(byKey.values());
  }, [brands]);

  // Fetch brands for sidebar
  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const [statsRes, brandsRes] = await Promise.all([
        apiFetch('/api/dashboard/stats', { credentials: 'include' }),
        apiFetch('/api/brands', { credentials: 'include' }),
      ]);
      if (statsRes.ok) {
        const data = await statsRes.json();
        setBrands(data.brand_performance || []);
      }
      if (brandsRes.ok) {
        const brandsList = await brandsRes.json();
        const logos = {};
        brandsList.forEach(b => { if (b.logo_url) logos[b.name] = b.logo_url; });
        setBrandLogos(logos);
      }
    } catch (err) {
      console.error('Error fetching navigation data:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Check if current path matches
  const isActivePath = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`fixed top-0 left-0 z-50 h-full w-60 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Logo - AURA */}
            <div 
              className="flex items-center justify-between h-14 px-4 border-b border-border cursor-pointer hover:bg-accent/30 transition-colors"
              onClick={() => navigate('/dashboard')}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#8b5cf6] to-[#2eb4ac] flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-lg tracking-tight">AURA</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden h-8 w-8"
                onClick={(e) => { e.stopPropagation(); setSidebarOpen(false); }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Navigation */}
            <ScrollArea className="flex-1 py-3">
              <nav className="px-2 space-y-1">
                {/* Overview Section */}
                <div className="px-2 py-1.5">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Overview</span>
                </div>
                
                <NavLink
                  to="/dashboard"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive && location.pathname === '/dashboard'
                        ? 'bg-[#8b5cf6]/10 text-[#8b5cf6]'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`
                  }
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </NavLink>

                <NavLink
                  to="/reviews"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-[#8b5cf6]/10 text-[#8b5cf6]'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`
                  }
                  data-testid="nav-reviews"
                >
                  <MessageSquare className="w-4 h-4" />
                  All Reviews
                </NavLink>

                <NavLink
                  to="/upload-reviews"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/upload-reviews'
                      ? 'bg-[#8b5cf6]/10 text-[#8b5cf6]'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`}
                  data-testid="nav-upload-reviews"
                >
                  <Upload className="w-4 h-4" />
                  Upload Reviews
                </NavLink>

                {/* Brands Section */}
                {visibleBrands.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 mt-4">
                      <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Brands</span>
                    </div>
                    
                    {/* Show first 4 brands always - deduped by case-insensitive name */}
                    {visibleBrands
                      .slice(0, expandedBrands ? visibleBrands.length : 4)
                      .map((brand) => (
                      <button
                        key={brand.brand}
                        data-testid={`sidebar-brand-${brand.brand.trim().toLowerCase()}`}
                        data-brand-item="1"
                        onClick={() => {
                          navigate(`/brand/${encodeURIComponent(brand.brand)}`);
                          setSidebarOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActivePath(`/brand/${brand.brand}`)
                            ? 'bg-[#2eb4ac]/10 text-[#2eb4ac]'
                            : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          {brandLogos[brand.brand] ? (
                            <img src={brandLogos[brand.brand]} alt="" className="w-4 h-4 rounded object-cover" />
                          ) : (
                            <Store className="w-4 h-4" />
                          )}
                          <span className="truncate max-w-[120px]">{brand.brand}</span>
                        </div>
                        <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                      </button>
                    ))}
                    
                    {/* Show more/less toggle */}
                    {visibleBrands.length > 4 && (
                      <button
                        onClick={() => setExpandedBrands(!expandedBrands)}
                        data-testid="sidebar-brands-toggle"
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedBrands ? 'rotate-180' : ''}`} />
                        {expandedBrands ? 'Show less' : `Show ${visibleBrands.length - 4} more`}
                      </button>
                    )}
                  </>
                )}

                {/* Settings Section */}
                <div className="px-2 py-1.5 mt-4">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Settings</span>
                </div>

                <NavLink
                  to="/settings"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-[#8b5cf6]/10 text-[#8b5cf6]'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`
                  }
                  data-testid="nav-upload"
                >
                  <Database className="w-4 h-4" />
                  Control Panel
                </NavLink>
              </nav>
            </ScrollArea>

            {/* User info */}
            <div className="p-3 border-t border-border">
              <div className="flex items-center gap-2.5 px-2">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-[#8b5cf6]/10 text-[#8b5cf6] text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="lg:pl-60">
          {/* Top navbar */}
          <header className="sticky top-0 z-30 h-14 glass border-b border-border/50">
            <div className="flex items-center justify-between h-full px-4 lg:px-6">
              {/* Left side */}
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  onClick={() => setSidebarOpen(true)}
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </div>

              {/* Right side */}
              <div className="flex items-center gap-1">
                {/* Theme toggle */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleTheme}
                      className="h-9 w-9"
                      data-testid="theme-toggle"
                    >
                      {theme === 'dark' ? (
                        <Sun className="w-4 h-4" />
                      ) : (
                        <Moon className="w-4 h-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Toggle theme</TooltipContent>
                </Tooltip>

                {/* Cost Tracker (discreet) */}
                <CostTrackerWidget />

                {/* What's New Bell */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-9 w-9 relative" data-testid="whats-new-bell">
                      <span className="inline-block animate-bell-ring">
                        <Bell className="w-4 h-4" />
                      </span>
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="bottom"
                    align="end"
                    sideOffset={8}
                    className="w-80 p-0 rounded-xl shadow-lg border border-border/60 bg-popover"
                    data-testid="whats-new-popover"
                  >
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50">
                      <Sparkles className="w-4 h-4 text-[#8b5cf6]" />
                      <span className="font-heading font-semibold text-sm">What's New</span>
                    </div>
                    <div className="max-h-[360px] overflow-y-auto">
                      {WHATS_NEW_ITEMS.map((item, i) => {
                        const Icon = item.icon;
                        return (
                          <div
                            key={i}
                            className="flex items-start gap-3 px-4 py-3 hover:bg-accent/40 transition-colors cursor-default"
                          >
                            <div className="mt-0.5 w-7 h-7 rounded-lg bg-accent/60 flex items-center justify-center shrink-0">
                              <Icon className="w-3.5 h-3.5 text-foreground/70" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-semibold leading-tight">{item.title}</span>
                                {item.isNew && (
                                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white leading-none shrink-0">
                                    NEW
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.description}</p>
                            </div>
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap mt-0.5 shrink-0">{item.date}</span>
                          </div>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* User menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2 px-2 h-9" data-testid="user-menu">
                      <Avatar className="w-7 h-7">
                        <AvatarImage src={user?.picture} />
                        <AvatarFallback className="bg-[#8b5cf6]/10 text-[#8b5cf6] text-xs">
                          {getInitials(user?.name)}
                        </AvatarFallback>
                      </Avatar>
                      <ChevronDown className="w-3.5 h-3.5 hidden sm:block opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/settings')}>
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="p-4 lg:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
